import { first, last } from 'lodash';
import { calculateTime } from './functions';

export const functionsMixin = {
  // ***************************************************************************************
  // getWordsByIndexLine() permet de récupérer les mots de la phrase entière
  // ***************************************************************************************
  getWordsByIndexLine(indexLine) {
    return this.script.filter(
      (x) => x.indexLine === indexLine || x.parentIndexLine === indexLine
    );
  },

  // ***************************************************************************************
  // getWordsByIndexes() permet de récupérer les mots à partir des index (début et fin)
  // ***************************************************************************************
  getWordsByIndexes(startIndex, endIndex) {
    return this.script.filter(
      (x) => x.index >= startIndex && x.index <= endIndex
    );
  },

  // ***************************************************************************************
  // setPositionForCursor() permet de positionner le cursor sur le bon mot, la bonne ligne et le bon caractère
  // ***************************************************************************************
  setPositionForCursor(word, firstLetter = true) {
    if (!word) {
      return;
    }

    this.currentWordIndex = word.index;
    this.currentLineIndex = word.indexLine;

    if (word.letters && word.letters.length) {
      if (firstLetter) {
        this.currentLetterIndex = first(word.letters).indexLetter;
      } else {
        this.currentLetterIndex = last(word.letters).indexLetter;
      }
    }

    if (
      word.y === 0 ||
      word.y === this.metricY ||
      this.height < word.y + this.lineHeight
    ) {
      this.scrollTo(word.indexLine);
    }

    this.onMove();
  },

  // ***************************************************************************************
  // setPositionOfSearch() est appelée quand on veut naviguer de mot en mot pendant la recherche
  // ***************************************************************************************
  setPositionOfSearch(currentPosition) {
     if (!this.wordsToSearch.length) {
      return;
    }

    const goToWord = this.wordsToSearch[currentPosition]?.words[0];

    if (!goToWord) {
      return;
    }

    this.currentWordIndex = goToWord.index;
    this.currentLineIndex = goToWord.indexLine;

    if (!goToWord.letters) {
      const line = this.script.filter(
        (x) => x.indexLine === goToWord.indexLine
      );
      this.addLettersToNewLine(line);
    }

    this.currentLetterIndex = first(goToWord.letters).indexLetter;

    if (
      goToWord.y === 0 ||
      goToWord.y === this.metricY ||
      this.height < goToWord.y + this.lineHeight
    ) {
      this.scrollTo(goToWord.indexLine);
    }

    this.onMove();
  },

  // ***************************************************************************************
  // clearCoordinates permet d'effacer les coordonnées pour qu'ils soient recalculés
  // ***************************************************************************************
  clearCoordinates() {
    this.script
      .filter((word) => word.x !== 0 && word.y !== 0)
      .forEach((word) => {
        word.x = 0;
        word.y = 0;
        word.startX = 0;
        word.endX = 0;
        word.startY = 0;
        word.endY = 0;
      });
  },

  // ***************************************************************************************
  // updateTotalWordsInError permet d'afficher le nombre de mots en erreur
  // ***************************************************************************************
  updateTotalWordsInError() {
    const wordsSuggested = this.script.filter(
      (w) => w.suggests && w.suggests.length
    );
    this.setState({
      totalWordsInError: wordsSuggested.length,
    });
  },

  // ***************************************************************************************
  // createWords permet de créer une liste de mots
  // ***************************************************************************************
  createWords(texts) {
    const { text, startTime, endTime, newline } = this.currentWord;

    const newWords = [];

    let inOutTime = startTime;

    texts.forEach((newText, index) => {
      const newWord = {
        ...this.currentWord,
        text: newText,
        newline: index === 0 ? newline : false,
        startTime: inOutTime,
        letters: [],
        markWord: false,
        cpbefore: false,
        cpafter: false,
        cpinside: false,
      };

      inOutTime = calculateTime(inOutTime, endTime, text, newText);
      newWord.endTime = inOutTime;

      if (index === texts.length - 1) {
        newWord.endTime = endTime;
      }

      newWords.push(newWord);
    });

    return newWords;
  },

  // ***************************************************************************************
  // groupWordByChar permet de regrouper deux mots par un caractère
  // ***************************************************************************************
  groupWordByChar(action, charCode, next) {
    const { text, index, newline, startTime } = this.currentWord;

    if (text.length > 36) {
      return;
    }

    if (next) {
      const nextWord = this.script[index + 1];
      if (
        !nextWord ||
        (charCode && nextWord.text.includes(charCode)) ||
        nextWord.newline
      ) {
        return;
      }

      // On historise
      this.addNewActionForUndo(action, index, 2, 1);

      this.currentWord.text += charCode + nextWord.text;
      this.currentWord.endTime = nextWord.endTime;

      this.script.splice(nextWord.index, 1);
    } else {
      const prevWord = this.script[index - 1];
      if (!prevWord || prevWord.text.includes(charCode) || newline) {
        return;
      }

      // On historise
      this.addNewActionForUndo(action, index - 1, 2, 1);

      prevWord.text += charCode + text;
      prevWord.endTime = this.currentWord.endTime;

      this.script.splice(index, 1);

      this.currentWordIndex = prevWord.index;
    }

    this.onChange(index, startTime);
  },
};
